import { z } from 'zod';

/**
 * Zod schemas for investment-related entities
 * These provide runtime validation and type inference
 */

// InvestmentProgram schema
export const investmentProgramSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Program name is required'),
  type: z.enum(['Fund', 'Syndication', 'Direct'] as const),
  status: z.enum(['Open', 'Closed', 'Coming Soon'] as const),
  targetIrr: z.number().nonnegative(),
  minInvestment: z.number().nonnegative(),
  targetRaise: z.number().nonnegative(),
  currentRaise: z.number().nonnegative(),
  image: z.string(),
  description: z.string(),
  location: z.string().optional(),
});

export type InvestmentProgram = z.infer<typeof investmentProgramSchema>;

// Investor schema — accept Prisma shape (investmentAmount, no totalInvested/avatar) and coerce to domain shape
const typeEnum = ['Individual', 'Institution', 'Fund'] as const;
const statusEnum = ['Active', 'Prospective'] as const;
export const investorSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Investor name is required'),
  email: z.string().email(),
  type: z.string().transform(t => (typeEnum.includes(t as (typeof typeEnum)[number]) ? t : 'Individual')) as z.ZodType<(typeof typeEnum)[number]>,
  totalInvested: z.number().nonnegative().optional().nullable(),
  entitiesInvested: z.number().int().nonnegative().optional().nullable(),
  distributionsYTD: z.number().nonnegative().optional().nullable(),
  status: z.string().optional().nullable(),
  avatar: z.string().optional().nullable(),
  entityIds: z.array(z.string()).optional().nullable(),
  userId: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  accreditationStatus: z.string().optional().nullable(),
  investmentAmount: z.union([z.number(), z.bigint()]).optional().nullable(),
  createdAt: z.union([z.string(), z.date()]).optional().nullable(),
  updatedAt: z.union([z.string(), z.date()]).optional().nullable(),
}).transform(o => ({
  id: o.id,
  name: o.name,
  email: o.email,
  type: (typeEnum.includes(o.type) ? o.type : 'Individual'),
  totalInvested: typeof o.totalInvested === 'number' ? o.totalInvested : (o.investmentAmount == null ? 0 : Number(o.investmentAmount)),
  entitiesInvested: typeof o.entitiesInvested === 'number' ? o.entitiesInvested : (Array.isArray(o.entityIds) ? o.entityIds.length : 0),
  distributionsYTD: o.distributionsYTD ?? 0,
  status: (statusEnum.includes((o.status ?? 'Active') as (typeof statusEnum)[number]) ? (o.status as (typeof statusEnum)[number]) : 'Active'),
  avatar: o.avatar ?? '',
  entityIds: Array.isArray(o.entityIds) ? o.entityIds : [],
  ...(o.userId !== undefined && o.userId !== null ? { userId: o.userId } : {}),
}));

export type Investor = z.infer<typeof investorSchema>;

// InvestmentEntity schema — API may return different casing or null for optional fields
const entityTypeEnum = ['direct-owner', 'fund', 'syndication', 'spv'] as const;
const entityStatusEnum = ['active', 'closed', 'forming'] as const;
export const investmentEntitySchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Entity name is required'),
  type: z.union([z.enum(entityTypeEnum), z.string()]).transform(t => (entityTypeEnum.includes(t as (typeof entityTypeEnum)[number]) ? t as (typeof entityTypeEnum)[number] : 'direct-owner')),
  ownerId: z.string(),
  propertyIds: z.preprocess((v) => (Array.isArray(v) ? v : []), z.array(z.string())),
  status: z.union([z.enum(entityStatusEnum), z.string()]).transform(s => (entityStatusEnum.includes((s ?? '').toLowerCase() as (typeof entityStatusEnum)[number]) ? (s ?? '').toLowerCase() as (typeof entityStatusEnum)[number] : 'active')),
  totalCapital: z.number().nonnegative().optional().nullable(),
  targetIrr: z.number().nonnegative().optional().nullable(),
  createdAt: z.string().optional().nullable(),
  updatedAt: z.string().optional().nullable(),
});

export type InvestmentEntity = z.infer<typeof investmentEntitySchema>;

// EntityInvestor schema (InvestmentEntityInvestor relationship)
export const entityInvestorSchema = z.object({
  id: z.string(),
  investorId: z.string(),
  entityId: z.string(),
  ownershipPercentage: z.number().min(0).max(100),
  capitalInvested: z.number().nonnegative(),
  capitalAccount: z.number(),
  distributionsReceived: z.number().nonnegative(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type EntityInvestor = z.infer<typeof entityInvestorSchema>;
export type InvestmentEntityInvestor = EntityInvestor;
